import * as React from "react";

import { FilterOptionControl } from "../common";

import { replaceAll } from "../utils";

import { PresentationFacet } from "../model";

import {
  usePlpPresenterForTheFilterMenu_Desktop,
  useProductListState,
  useProductListController_forFiltering,
} from "../providers";

interface FilterProps {
  presentationFacet: PresentationFacet;
}

const MAX_ITEMS = 10;

export const Filter: React.FunctionComponent<FilterProps> = (props) => {
  const { presentationFacet } = props;

  const { selectedOptions } = useProductListState();
  const { isOptionSelected } = useProductListController_forFiltering();

  const {
    whatIsTheExclusivelyOpenFilterSubMenuRightNow_onDesktop,
    handleActionFilterOption_onDesktop,
    handleTogglingAFilterSubMenu_onDesktop,
  } = usePlpPresenterForTheFilterMenu_Desktop();

  const [showAll, setShowAll] = React.useState(false);

  React.useEffect(() => {
    if (!isKeyAvailable(presentationFacet.key)) {
      setShowAll(false);
    }
  }, [
    presentationFacet,
    whatIsTheExclusivelyOpenFilterSubMenuRightNow_onDesktop,
  ]);

  const filterOptions = React.useMemo(() => {
    const filterOptions = presentationFacet.potentialOptions;
    return filterOptions.map((filterOption, index) => {
      if (index >= MAX_ITEMS && !showAll) {
        return null;
      }

      const isSelected = isOptionSelected(presentationFacet, filterOption);

      const action: React.MouseEventHandler = () => {
        if (isKeyAvailable(presentationFacet.key)) {
          handleActionFilterOption_onDesktop(presentationFacet, filterOption);
        }
      };

      return (
        <div
          key={filterOption.id}
          className={`option ${isSelected ? "selected" : ""}`}
          onClick={action}
          aria-label={`filter option: ${filterOption.name}`}
        >
          <FilterOptionControl
            facet={presentationFacet}
            option={filterOption}
            renderExtraLabelSpanSlot={() => (
              <React.Fragment>
                &nbsp;<span className="count">({filterOption.count})</span>
              </React.Fragment>
            )}
          />
        </div>
      );
    });
  }, [
    presentationFacet,
    selectedOptions,
    showAll,
    whatIsTheExclusivelyOpenFilterSubMenuRightNow_onDesktop,
  ]);

  const showAllOrLess = React.useMemo(() => {
    const filterOptions = presentationFacet.potentialOptions;
    if (filterOptions.length < MAX_ITEMS) {
      return null;
    }

    const onShowLess = () => {
      setShowAll(false);
    };

    const onShowMore = () => {
      setShowAll(true);
    };

    return (
      <React.Fragment>
        {showAll ? (
          <button className="sub-menu-expander show-less" onClick={onShowLess}>
            - Show Less
          </button>
        ) : (
          <button className="sub-menu-expander show-more" onClick={onShowMore}>
            + Show All
          </button>
        )}
      </React.Fragment>
    );
  }, [presentationFacet, showAll]);

  const howManySelectedOptionsOnFilter = React.useMemo(() => {
    let howManyOfMyOptionsAreSelected = 0;
    selectedOptions.forEach((option) => {
      const isThisSelectedOptionInOurFilterOptions =
        option.facetName === presentationFacet.key;
      if (isThisSelectedOptionInOurFilterOptions) {
        howManyOfMyOptionsAreSelected = howManyOfMyOptionsAreSelected + 1;
      }
    });
    return howManyOfMyOptionsAreSelected;
  }, [presentationFacet, selectedOptions]);

  const onTitleContainerActioned = () => {
    handleTogglingAFilterSubMenu_onDesktop(presentationFacet);
  };

  const isKeyAvailable = (key: any) => {
    return whatIsTheExclusivelyOpenFilterSubMenuRightNow_onDesktop ?
      whatIsTheExclusivelyOpenFilterSubMenuRightNow_onDesktop.split(',').includes(key) : false;
  }

  return (
    <li
      className={`filter ${
        presentationFacet.hasSubMenu ? "has-sub-menu" : ""
      } ${
        presentationFacet.hasSubMenu &&
        isKeyAvailable(presentationFacet.key)
          ? "open"
          : ""
      } ${showAll ? "extra-expansion" : ""}`}
      aria-label={`${replaceAll(presentationFacet.title, "_", " ")} filter`}
    >
      <button
        aria-label={`${
          presentationFacet.hasSubMenu &&
          isKeyAvailable(presentationFacet.key)
            ? "close"
            : "open"
        } the ${replaceAll(presentationFacet.title, "_", " ")} options menu`}
        className="title-container"
        onClick={onTitleContainerActioned}
      >
        <p>{replaceAll(presentationFacet.title, "_", " ")}</p>
        <div className="right">
          {howManySelectedOptionsOnFilter > 0 ? (
            <div className="applied-options">
              {howManySelectedOptionsOnFilter} applied
            </div>
          ) : null}
        </div>
      </button>
      <div
        aria-label={`filter options menu which is currently ${
          presentationFacet.hasSubMenu &&
          isKeyAvailable(presentationFacet.key)
            ? "open"
            : "collapsed"
        }`}
        className="sub-menu"
      >
        {filterOptions}
        {showAllOrLess}
      </div>
    </li>
  );
};
